import './ScreenShareProvider.scss';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { participantColor, takeSuspendedShare, unlockSupportCues } from '@srm/screen-sharing/light';
import { createValueStore } from '@srm/ui/lib/valueStore';
import { isSupportSession } from '@/auth/supportSession';
import { ConfigService } from '@/configuration/services/configService';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useDocumentTitlePrefix } from '@hooks/useDocumentTitlePrefix';
import SupportFrame from '@atoms/SupportFrame';
import ScreenShareBar from '@molecules/ScreenShareBar';
import { resolveScreenShareFrame } from '@molecules/ScreenShareBar/screenShareBarState';
import {
    inertScreenShare,
    IScreenShareContextValue,
    SCREEN_SHARE_TITLE_PREFIX,
    ScreenShareActiveContext,
    ScreenShareAvailableContext,
    ScreenShareContext,
} from './ScreenShareContext';
import { clearSupportErrors, installSupportErrorCapture } from '@utils/supportErrorLog';
import { clearSupportRequests } from '@utils/supportNetworkLog';
import { installSupportLongTasks } from '@utils/supportPerformance';
import type { RootState } from '@/store';
import { versionUpdate } from '@utils/versionUpdate';
import { useScreenShare } from './useScreenShare';
import { useRemoteControlInputBlock } from './useRemoteControlInputBlock';
import { shortPeerName } from './supportPresence';
import SupportPresenceOverlay from './SupportPresenceOverlay/SupportPresenceOverlay';
import SupportLiveRegion from './SupportLiveRegion';
import SupportChatDock from './SupportChatDock';
import SupportBarPopups from './SupportBarPopups';

/**
 * Показ экрана поддержке на уровне приложения (`index.tsx`): переживает перемонтирование оболочки, а пункт меню
 * «Помощь» только запускает и останавливает его. Рисует рамку окна, подсказку у курсора, приставку заголовка,
 * присутствие сотрудника (курсор, рябь, кольцо, обводку поля) и глушит ввод пользователя на время управления.
 * Во вкладке «от имени» показа нет — провайдер инертен.
 */
export default function ScreenShareProvider({ children }: { children: ReactNode }) {
    if (isSupportSession()) return <ScreenShareContext value={inertScreenShare}>{children}</ScreenShareContext>;
    return <ActiveScreenShareProvider>{children}</ActiveScreenShareProvider>;
}

function ActiveScreenShareProvider({ children }: { children: ReactNode }) {
    const authenticated = useTypedSelector((state) => state.auth.isAuth);
    const userId = useTypedSelector((state) => state.auth.user?.id);
    const canViewAdminPanel = useTypedSelector((state) => !!state.auth.user?.canView?.adminPanel);
    const available = canViewAdminPanel || !ConfigService.get().application.supportScreenShare?.adminsOnly;
    const store = useStore<RootState>();
    const share = useScreenShare(userId === undefined ? undefined : String(userId), store.getState);
    const { active, stop, restore, revokeControl, dismissError, dismissFeedback } = share;
    const [slots, setSlots] = useState(0);
    const [chatOpened, setChatOpened] = useState(false);
    const [chatDraft] = useState(() => createValueStore(''));
    useEffect(() => {
        if (active) return;
        setChatOpened(false);
        chatDraft.set('');
    }, [active, chatDraft]);

    // Последние ошибки страницы — в контекст для поддержки (с незадолго до начала показа); долгие задачи — в пакет
    // диагностики (§43.2).
    useEffect(() => installSupportErrorCapture(), []);
    useEffect(() => installSupportLongTasks(), []);
    // Выход или смена пользователя: показ, его ошибка и журнал ошибок прежней учётки следующему не достаются. Загрузка
    // профиля после входа (`userId` появился) сменой не считается.
    const owner = useRef({ authenticated, userId });
    useEffect(() => {
        const previous = owner.current;
        owner.current = { authenticated, userId };
        const loggedOut = previous.authenticated && !authenticated;
        const switched = previous.userId !== undefined && userId !== undefined && previous.userId !== userId;
        if (!loggedOut && !switched) return;
        clearSupportErrors();
        clearSupportRequests();
        stop();
        dismissError();
        dismissFeedback();
    }, [authenticated, userId, stop, dismissError, dismissFeedback]);

    // Страница обновилась во время показа (§35): вернуть его приостановленным — чат сразу, захват по нажатию. Запись
    // берётся один раз и только своя: без входа (и пока профиль не загружен) она ждёт в этой вкладке, чужую учётку
    // не касается.
    useEffect(() => {
        if (!authenticated || userId === undefined) return;
        const saved = takeSuspendedShare({ owner: String(userId) });
        if (saved) void restore(saved);
    }, [authenticated, userId, restore]);

    // Любой щелчок или клавиша во время показа разрешают странице звук (сигналы сеанса): после перезагрузки страницы
    // жеста «Показать экран» не было.
    useEffect(() => {
        if (!active) return undefined;
        const unlock = () => unlockSupportCues();
        document.addEventListener('pointerdown', unlock, true);
        document.addEventListener('keydown', unlock, true);
        return () => {
            document.removeEventListener('pointerdown', unlock, true);
            document.removeEventListener('keydown', unlock, true);
        };
    }, [active]);

    // Пока идёт показ, предложение «Обновить страницу» ждёт его конца: перезагрузка прервала бы показ.
    useEffect(() => {
        versionUpdate.hold(active);
        return () => versionUpdate.hold(false);
    }, [active]);

    const registerBarSlot = useCallback(() => {
        setSlots((count) => count + 1);
        return () => setSlots((count) => count - 1);
    }, []);

    const value = useMemo<IScreenShareContextValue>(
        () => ({ ...share, available, registerBarSlot, chatOpened, setChatOpened, chatDraft }),
        [share, available, registerBarSlot, chatOpened, chatDraft],
    );

    // С нажатия «Разрешить» (окно на сервере ещё открывается) и до отзыва ввод пользователя не доходит до страницы.
    const controlled =
        share.control.phase === 'granted' || (share.control.phase === 'requested' && !!share.control.allowing);
    const peerName = share.control.peerName || share.viewerName || 'сотрудник поддержки';
    const hint = useRemoteControlInputBlock({ active: controlled, peerName, onRelease: revokeControl });
    useDocumentTitlePrefix(active && !share.suspended ? SCREEN_SHARE_TITLE_PREFIX : null);
    const frame = resolveScreenShareFrame(share);
    const people = useMemo(
        () =>
            Object.fromEntries(
                share.participants.map((person) => [
                    person.peerId,
                    { name: person.name, color: participantColor(person.color) },
                ]),
            ),
        [share.participants],
    );

    return (
        <ScreenShareAvailableContext value>
            <ScreenShareActiveContext value={active}>
                <ScreenShareContext value={value}>
                    <SupportLiveRegion />
                    {children}
                    {slots === 0 && <ScreenShareBar floating />}
                    <SupportChatDock />
                    <SupportBarPopups userId={userId} />
                    {frame && <SupportFrame tone={frame} />}
                    {share.active && !share.suspended && (
                        <SupportPresenceOverlay
                            presence={share.presence}
                            tone={share.control.phase === 'granted' ? 'control' : 'view'}
                            name={shortPeerName(peerName)}
                            people={people}
                            area={share.captureArea}
                            plain={!share.restricted || share.cropped}
                            control={share.control.phase === 'granted'}
                            watched={!!share.viewerName}
                            sendCursor={share.sendCursor}
                            sendClickMark={share.sendClickMark}
                        />
                    )}
                    {hint && (
                        <div
                            className="remote-control-hint"
                            role="status"
                            data-remote-control-exclude
                            style={{ left: hint.x, top: hint.y }}
                        >
                            {hint.text}
                        </div>
                    )}
                </ScreenShareContext>
            </ScreenShareActiveContext>
        </ScreenShareAvailableContext>
    );
}
